import { REQUEST_STATUS } from "@/ts/enums/RequestStatus";
import {
    AssignmentInterface,
    MarkedAssignmentInterface
} from "@/ts/interfaces/MarkingPortal/AssignmentInterface";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { getStudentAssignmentDetailsThunk } from "../thunks";

interface AssignmentDetailsState {
    status: REQUEST_STATUS;
    error: string | null;
    data: AssignmentInterface | null;
    /**
     * The submission Portal flagged as the mark of record (BLD1-1143,
     * REP-ASG-9). Null when no submission is flagged — the page still
     * composes (REP-ASG-8).
     */
    markOfRecordSubmission: MarkedAssignmentInterface | null;
}

const initialState: AssignmentDetailsState = {
    status: REQUEST_STATUS.IDLE,
    error: null,
    data: null,
    markOfRecordSubmission: null
};

const assignmentDetailsSlice = createSlice({
    name: "reporting/assignmentDetails",
    initialState,
    reducers: {
        resetAssignmentDetailsState: () => initialState,
        clearAssignmentDetails: state => {
            state.status = REQUEST_STATUS.IDLE;
            state.error = null;
            state.data = null;
            state.markOfRecordSubmission = null;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(getStudentAssignmentDetailsThunk.pending, state => {
                state.status = REQUEST_STATUS.PENDING;
                state.error = null;
            })
            .addCase(
                getStudentAssignmentDetailsThunk.fulfilled,
                (
                    state,
                    action: PayloadAction<{
                        data: AssignmentInterface;
                        markOfRecordSubmission: MarkedAssignmentInterface | null;
                    }>
                ) => {
                    state.status = REQUEST_STATUS.SUCCESS;
                    state.data = action.payload.data;
                    state.markOfRecordSubmission =
                        action.payload.markOfRecordSubmission;
                }
            )
            .addCase(
                getStudentAssignmentDetailsThunk.rejected,
                (state, action) => {
                    state.status = REQUEST_STATUS.ERROR;
                    state.error =
                        (action.payload as string) ??
                        action.error.message ??
                        "Unknown error";
                    state.data = null;
                    state.markOfRecordSubmission = null;
                }
            );
    }
});

export const { resetAssignmentDetailsState, clearAssignmentDetails } =
    assignmentDetailsSlice.actions;

export default assignmentDetailsSlice.reducer;
